import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import { authAPI } from "../services/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  }, []);

  const verifyToken = useCallback(async () => {
    try {
      const response = await authAPI.verify();

      // A valid token for a non-super_admin account (e.g. a shop owner's)
      // must never sit here signed in — this app is platform-only.
      if (response.data.user?.role !== "super_admin") {
        logout();
        return;
      }

      setUser(response.data.user);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (identifier, password) => {
    try {
      const response = await authAPI.login(identifier, password);
      const { user: loggedInUser, token: newToken } = response.data;

      if (loggedInUser?.role !== "super_admin") {
        return {
          success: false,
          error: "This account is not a platform administrator.",
        };
      }

      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(loggedInUser));

      setToken(newToken);
      setUser(loggedInUser);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Sign-in failed",
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
